import { useEffect, useRef, useState } from 'react'
import { FaChevronDown, FaDownload, FaPlus, FaSyncAlt, FaTrash } from 'react-icons/fa'
import {
    Banner,
    Button,
    ButtonGroup,
    Card,
    CardGrid,
    CaseStatePill,
    Checkbox,
    CodeBlock,
    CollapsibleSection,
    colors,
    Column,
    DataTable,
    DatePicker,
    Drawer,
    EmptyState,
    Field,
    FieldGrid,
    FileInput,
    IconButton,
    Input,
    JsonViewer,
    KeyValueList,
    LoadingOverlay,
    Menu,
    MenuItem,
    MenuLabel,
    Modal,
    NativeSelect,
    Page,
    PageHeader,
    Pagination,
    ProgressBar,
    ReactSelect,
    SearchInput,
    Section,
    SegmentedControl,
    Spinner,
    StackCell,
    StatCard,
    StatGrid,
    StatusPill,
    Steps,
    Switch,
    Tabs,
    Textarea,
    Toolbar,
    ResultLine,
} from '../../../ui'
import { SPACING_STEPS, SWATCHES, TYPE_SCALE, USAGE_RULES } from './catalog'
import { downloadStyleGuide, StyleGuideDownloadKind } from './download'
import s from './styles.module.scss'

interface DemoRow {
    id: number
    client: string
    email: string
    lender: string
    claims: number
    status: 'active' | 'cancelled' | 'duplicate'
    created: string
}

const ROWS: DemoRow[] = [
    { id: 1, client: 'Amelia Hart', email: 'amelia.hart@example.com', lender: 'Black Horse', claims: 3, status: 'active', created: '2026-08-02' },
    { id: 2, client: 'Ben Okafor', email: 'ben.okafor@example.com', lender: 'Santander Consumer', claims: 1, status: 'cancelled', created: '2026-08-11' },
    { id: 3, client: 'Chloe Dunn', email: 'chloe.dunn@example.com', lender: 'MotoNovo', claims: 5, status: 'active', created: '2026-08-19' },
    { id: 4, client: 'Dev Patel', email: 'dev.patel@example.com', lender: 'Close Brothers', claims: 2, status: 'duplicate', created: '2026-09-01' },
]

const TONE: Record<DemoRow['status'], 'good' | 'bad' | 'warn'> = { active: 'good', cancelled: 'bad', duplicate: 'warn' }

const COLUMNS: Column<DemoRow>[] = [
    { key: 'client', header: 'Client', sortable: true, render: (r) => <StackCell primary={r.client} secondary={r.email} /> },
    { key: 'lender', header: 'Lender', sortable: true },
    { key: 'claims', header: 'Claims', align: 'right', sortable: true },
    { key: 'status', header: 'Status', render: (r) => <StatusPill tone={TONE[r.status]} dot>{r.status}</StatusPill> },
    { key: 'created', header: 'Created', nowrap: true, sortable: true },
]

export const StyleGuideView = () => {
    const [tab, setTab] = useState('buttons')
    const [seg, setSeg] = useState('all')
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [modal, setModal] = useState(false)
    const [drawer, setDrawer] = useState(false)
    const [busy, setBusy] = useState(false)
    const [date, setDate] = useState<Date | null>(null)
    const [selected, setSelected] = useState<number | null>(null)
    const [on, setOn] = useState(true)
    const [downloadOpen, setDownloadOpen] = useState(false)
    const downloadRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!downloadOpen) return
        const onPointer = (event: MouseEvent) => {
            if (!downloadRef.current?.contains(event.target as Node)) setDownloadOpen(false)
        }
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setDownloadOpen(false)
        }
        document.addEventListener('mousedown', onPointer)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onPointer)
            document.removeEventListener('keydown', onKey)
        }
    }, [downloadOpen])

    const download = (kind: StyleGuideDownloadKind) => {
        downloadStyleGuide(kind)
        setDownloadOpen(false)
    }

    return (
        <Page>
            <PageHeader
                eyebrow="Admin"
                title="Style guide"
                intro="Living reference for the dashboard design system. Every component here is exported from `ui`; pages should compose these rather than write their own."
                meta={
                    <>
                        <span>
                            Import path <code className={s.code}>import {'{ Button }'} from 'ui'</code>
                        </span>
                        <span>
                            SCSS <code className={s.code}>@use '../ui/styles' as *;</code>
                        </span>
                    </>
                }
                actions={
                    <div className={s.menuWrap} ref={downloadRef}>
                        <Button
                            variant="primary"
                            icon={<FaDownload />}
                            iconRight={<FaChevronDown />}
                            aria-haspopup="menu"
                            aria-expanded={downloadOpen}
                            onClick={() => setDownloadOpen((open) => !open)}
                        >
                            Download
                        </Button>
                        {downloadOpen && (
                            <Menu className={s.downloadMenu} aria-label="Download style guide">
                                <MenuLabel>For agents</MenuLabel>
                                <MenuItem trailing=".md" onClick={() => download('markdown')}>
                                    Markdown spec
                                </MenuItem>
                                <MenuItem trailing=".xml" onClick={() => download('xml')}>
                                    Structured XML
                                </MenuItem>
                                <MenuLabel>For other systems</MenuLabel>
                                <MenuItem trailing=".xls" onClick={() => download('excel')}>
                                    Excel workbook
                                </MenuItem>
                                <MenuItem trailing=".pdf" onClick={() => download('pdf')}>
                                    Printable PDF
                                </MenuItem>
                            </Menu>
                        )}
                    </div>
                }
            />

            <Section title="Usage rules" description="The short version of README.md in src/ui.">
                <Card>
                    {USAGE_RULES.map(([k, v]) => (
                        <div key={k} className={s.rule}>
                            <strong>{k}</strong>
                            <span>{v}</span>
                        </div>
                    ))}
                </Card>
            </Section>

            <Section title="Colour" description="Slate accent, one grey ladder, muted semantic colours.">
                <div className={s.stack}>
                    {SWATCHES.map((group) => (
                        <Card key={group.group} title={group.group} padding="sm">
                            <div className={s.swatches}>
                                {group.items.map(([name, hex]) => (
                                    <div key={name} className={s.swatch}>
                                        <div className={s.chip} style={{ background: hex }} />
                                        <span className={s.swatchName}>{name}</span>
                                        <span className={s.swatchHex}>{hex}</span>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    ))}
                </div>
            </Section>

            <Section title="Typography and spacing">
                <div className={s.two}>
                    <Card title="Type scale" subtitle="Poppins 400 / 500 / 600">
                        {TYPE_SCALE.map((row) => (
                            <div key={row.label} className={s.typeRow}>
                                <div>
                                    <div style={{ fontSize: 12.5, fontWeight: 500 }}>{row.label}</div>
                                    <div className={s.typeMeta}>{row.meta}</div>
                                </div>
                                <div>
                                    <span style={row.style}>{row.sample}</span>
                                </div>
                            </div>
                        ))}
                    </Card>
                    <Card title="Spacing and shape" subtitle="4px base scale; 10px card radius, 6px controls">
                        <div className={s.spacing}>
                            {SPACING_STEPS.map((n) => (
                                <div key={n} className={s.spaceBlock}>
                                    <span style={{ width: n, height: n }} />
                                    <span>{n}</span>
                                </div>
                            ))}
                        </div>
                        <div className={s.row} style={{ marginTop: 20 }}>
                            <div style={{ width: 80, height: 48, borderRadius: 10, border: `1px solid ${colors.line}`, background: colors.surface }} />
                            <div style={{ width: 80, height: 40, borderRadius: 6, border: `1px solid ${colors.line}`, background: colors.surface }} />
                            <div style={{ width: 80, height: 24, borderRadius: 999, border: `1px solid ${colors.line}`, background: colors.surface3 }} />
                            <span style={{ fontSize: 12, color: colors.ink3 }}>card 10 · control 6 · pill 999</span>
                        </div>
                    </Card>
                </div>
            </Section>

            <Section title="Components">
                <Tabs
                    value={tab}
                    onChange={setTab}
                    items={[
                        { value: 'buttons', label: 'Buttons' },
                        { value: 'data', label: 'Data display' },
                        { value: 'table', label: 'Table', count: ROWS.length },
                        { value: 'forms', label: 'Forms' },
                        { value: 'layout', label: 'Layout' },
                        { value: 'feedback', label: 'Feedback' },
                    ]}
                />

                {tab === 'buttons' && (
                    <div className={s.stack}>
                        <Card title="Variants">
                            <div className={s.row}>
                                <Button variant="primary">Primary</Button>
                                <Button>Neutral</Button>
                                <Button variant="danger">Danger</Button>
                                <Button variant="ghost">Ghost</Button>
                                <Button variant="link">Link</Button>
                                <Button variant="primary" disabled>
                                    Disabled
                                </Button>
                                <Button variant="primary" loading>
                                    Loading
                                </Button>
                            </div>
                        </Card>
                        <Card title="Sizes, icons and groups">
                            <div className={s.row}>
                                <Button size="sm" icon={<FaDownload />}>
                                    Export CSV
                                </Button>
                                <Button size="sm" variant="danger" icon={<FaTrash />}>
                                    Remove
                                </Button>
                                <IconButton label="Refresh" icon={<FaSyncAlt />} />
                                <IconButton label="Delete" icon={<FaTrash />} variant="danger" size="sm" />
                                <ButtonGroup attached>
                                    <Button>Day</Button>
                                    <Button>Week</Button>
                                    <Button>Month</Button>
                                </ButtonGroup>
                                <SegmentedControl
                                    value={seg}
                                    onChange={setSeg}
                                    options={[
                                        { value: 'all', label: 'All', count: 128 },
                                        { value: 'active', label: 'Active', count: 96 },
                                        { value: 'cancelled', label: 'Cancelled', count: 32 },
                                    ]}
                                />
                            </div>
                        </Card>
                    </div>
                )}

                {tab === 'data' && (
                    <div className={s.stack}>
                        <StatGrid>
                            <StatCard label="Signed clients" value={12480} sub="All introducers" />
                            <StatCard label="Claims found" value={38215} delta={412} deltaLabel="this week" />
                            <StatCard label="Acceptance rate" value={92.4} unit="%" tone="good" />
                            <StatCard label="Rejected" value={188} tone="bad" delta={-14} />
                            <StatCard label="Pending review" value={27} tone="warn" />
                            <StatCard label="Never batched" value={1043} tone="muted" sub="Purgeable" />
                        </StatGrid>
                        <div className={s.two}>
                            <Card title="Status pills">
                                <div className={s.row}>
                                    <StatusPill>Neutral</StatusPill>
                                    <StatusPill tone="accent">Accent</StatusPill>
                                    <StatusPill tone="good" dot>
                                        Active
                                    </StatusPill>
                                    <StatusPill tone="bad" dot>
                                        Cancelled
                                    </StatusPill>
                                    <StatusPill tone="warn" dot>
                                        Duplicate
                                    </StatusPill>
                                    <StatusPill tone="info">Exported</StatusPill>
                                    <StatusPill tone="outline">Outline</StatusPill>
                                    <StatusPill round tone="accent">
                                        12
                                    </StatusPill>
                                    <StatusPill upper tone="good" size="sm">
                                        Signed
                                    </StatusPill>
                                    <CaseStatePill cancelled={0} />
                                    <CaseStatePill cancelled={1} />
                                    <CaseStatePill marked_duplicate={1} />
                                </div>
                            </Card>
                            <Card title="Key / value">
                                <KeyValueList
                                    items={[
                                        { label: 'Client ref', value: 'SL-0048213', mono: true },
                                        { label: 'Email', value: 'amelia.hart@example.com' },
                                        { label: 'Lender', value: 'Black Horse' },
                                        { label: 'Court claim no.', value: null },
                                    ]}
                                />
                            </Card>
                            <Card title="Key / value (stacked)" subtitle="Narrow panels: full-width values.">
                                <KeyValueList
                                    stacked
                                    items={[
                                        { label: 'Email', value: 'amelia.hart-worthington@sentinellegal.co.uk' },
                                        { label: 'Primary organisation', value: 'Sentinel' },
                                        { label: 'User ID', value: '165202c4-70a1-70b0-10af-47e1c6abf36b', mono: true },
                                    ]}
                                />
                            </Card>
                        </div>
                        <div className={s.two}>
                            <Card title="Progress">
                                <div className={s.stack}>
                                    <ProgressBar value={64} label="Batch allocation" />
                                    <ProgressBar value={92} tone="good" label="Signed" />
                                    <ProgressBar value={18} tone="bad" label="Rejected" thin />
                                    <Steps current={2} steps={[{ label: 'Signed', meta: '02 Aug' }, { label: 'LOA sent', meta: '05 Aug' }, { label: 'Credit report' }, { label: 'LBA' }, { label: 'Settled' }]} />
                                </div>
                            </Card>
                            <Card title="JSON viewer">
                                <JsonViewer title="Payload" data={{ client: ROWS[0], agreements: [{ lender: 'Black Horse', account_id: '00123', status: 'settled' }] }} maxHeight={200} />
                            </Card>
                        </div>
                    </div>
                )}

                {tab === 'table' && (
                    <div className={s.stack}>
                        <Toolbar
                            end={
                                <>
                                    <Button size="sm" icon={<FaDownload />}>
                                        Export
                                    </Button>
                                    <Button size="sm" variant="primary" icon={<FaPlus />}>
                                        Add client
                                    </Button>
                                </>
                            }
                        >
                            <SearchInput value={search} onChange={setSearch} placeholder="Search clients…" style={{ minWidth: 260 }} />
                            <NativeSelect small options={[{ value: '', label: 'All lenders' }, { value: 'bh', label: 'Black Horse' }]} defaultValue="" />
                            <SegmentedControl value={seg} onChange={setSeg} options={[{ value: 'all', label: 'All' }, { value: 'active', label: 'Active' }, { value: 'cancelled', label: 'Cancelled' }]} />
                        </Toolbar>
                        <ResultLine>Showing {ROWS.length} of 128 clients</ResultLine>
                        <DataTable
                            columns={COLUMNS}
                            rows={ROWS.filter((r) => !search || r.client.toLowerCase().includes(search.toLowerCase()))}
                            rowKey={(r) => r.id}
                            selectedKey={selected}
                            onRowClick={(r) => setSelected(r.id === selected ? null : r.id)}
                            expandedKeys={selected != null ? [selected] : []}
                            renderExpanded={(r) => <KeyValueList columns items={[{ label: 'Client ref', value: `SL-${r.id.toString().padStart(6, '0')}`, mono: true }, { label: 'Lender', value: r.lender }, { label: 'Claims', value: r.claims }, { label: 'Created', value: r.created }]} />}
                            rowTone={(r) => (r.status === 'cancelled' ? 'bad' : undefined)}
                            footer={<Pagination page={page} pageSize={25} total={128} onChange={setPage} onPageSizeChange={() => undefined} />}
                        />
                        <div className={s.two}>
                            <DataTable columns={COLUMNS} rows={[]} rowKey={(r) => r.id} emptyTitle="No clients match" emptyDescription="Try a different lender or clear the search." emptyAction={<Button size="sm">Clear filters</Button>} />
                            <DataTable columns={COLUMNS} rows={[]} rowKey={(r) => r.id} loading />
                        </div>
                    </div>
                )}

                {tab === 'forms' && (
                    <Card title="Client details" subtitle="Field + FieldGrid with every control type" actions={<Switch checked={on} onChange={(e) => setOn(e.target.checked)} label="Active" />}>
                        <FieldGrid>
                            <Field label="First name" required>
                                {(id) => <Input id={id} placeholder="Amelia" />}
                            </Field>
                            <Field label="Last name" required>
                                {(id) => <Input id={id} placeholder="Hart" />}
                            </Field>
                            <Field label="Email" error="Enter a valid email address">
                                {(id) => <Input id={id} type="email" invalid defaultValue="amelia.hart@" />}
                            </Field>
                            <Field label="Phone" hint="UK numbers only, +44 format">
                                {(id) => <Input id={id} type="tel" placeholder="+447123456789" />}
                            </Field>
                            <Field label="Date of birth">
                                <DatePicker selected={date} onChange={(d) => setDate(d as Date | null)} />
                            </Field>
                            <Field label="Lender" optional>
                                <ReactSelect
                                    isClearable
                                    placeholder="Search lenders…"
                                    options={[
                                        { value: 'bh', label: 'Black Horse' },
                                        { value: 'sc', label: 'Santander Consumer' },
                                        { value: 'mn', label: 'MotoNovo' },
                                    ]}
                                />
                            </Field>
                            <Field label="Introducer">
                                {(id) => <NativeSelect id={id} placeholder="Choose…" options={[{ value: 'srds', label: 'SRDS' }, { value: 'mfc', label: 'Evans Hughes' }]} defaultValue="" />}
                            </Field>
                            <Field label="Search">{(id) => <SearchInput id={id} value={search} onChange={setSearch} />}</Field>
                            <Field label="Supporting document" span={2}>
                                <FileInput onChange={() => undefined} block placeholder="Choose a PDF or image…" />
                            </Field>
                            <Field label="Notes" span="all">
                                {(id) => <Textarea id={id} placeholder="Anything the case handler should know" />}
                            </Field>
                            <Field span="all">
                                <div className={s.row}>
                                    <Checkbox label="Send welcome email" hint="Uses the introducer's template" defaultChecked />
                                    <Checkbox label="Send SMS" />
                                    <Checkbox label="Disabled" disabled />
                                </div>
                            </Field>
                        </FieldGrid>
                    </Card>
                )}

                {tab === 'layout' && (
                    <div className={s.stack}>
                        <CardGrid minColumn={240}>
                            <Card title="Plain card" subtitle="Default padding">
                                Body content goes here.
                            </Card>
                            <Card title="Muted card" muted>
                                Secondary panel.
                            </Card>
                            <Card title="Interactive" interactive actions={<StatusPill tone="accent">New</StatusPill>}>
                                Hover to see the affordance.
                            </Card>
                            <Card title="Accent top" accentTop footer={<span>Footer slot</span>}>
                                Hero-style card. Edge accents are square-cornered.
                            </Card>
                            <Card title="Good tone" tone="good">
                                Left border tone, square corners.
                            </Card>
                            <Card title="Bad tone" tone="bad">
                                Left border tone, square corners.
                            </Card>
                        </CardGrid>
                        <CollapsibleSection title="Group section" meta="3 items" variant="group" defaultOpen>
                            <CollapsibleSection title="Nested subsection" meta="closed by default">
                                <p style={{ margin: 0, fontSize: 13 }}>Lazy-mounted content.</p>
                            </CollapsibleSection>
                            <CollapsibleSection title="Another subsection" actions={<Button size="sm">Action</Button>}>
                                <p style={{ margin: 0, fontSize: 13 }}>Header actions do not toggle the section.</p>
                            </CollapsibleSection>
                        </CollapsibleSection>
                        <CollapsibleSection title="Filters panel" variant="panel" defaultOpen>
                            <Toolbar>
                                <SearchInput value={search} onChange={setSearch} />
                                <NativeSelect small options={[{ value: '', label: 'Any status' }]} defaultValue="" />
                            </Toolbar>
                        </CollapsibleSection>
                        <CollapsibleSection title="Flat section" variant="flat" defaultOpen>
                            <p style={{ margin: 0, fontSize: 13 }}>Hairline divider, unboxed body. Used on detail pages.</p>
                        </CollapsibleSection>
                        <div className={s.row}>
                            <Button onClick={() => setModal(true)}>Open modal</Button>
                            <Button onClick={() => setDrawer(true)}>Open drawer</Button>
                            <Button
                                onClick={() => {
                                    setBusy(true)
                                    setTimeout(() => setBusy(false), 1200)
                                }}
                            >
                                Loading overlay
                            </Button>
                        </div>
                        <LoadingOverlay loading={busy} text="Refreshing…">
                            <Card title="Overlay host">Content is veiled while loading.</Card>
                        </LoadingOverlay>
                        <div style={{ display: 'flex', gap: 24 }}>
                            <Tabs orientation="vertical" value={seg} onChange={setSeg} items={[{ value: 'all', label: 'Details' }, { value: 'active', label: 'Milestones' }, { value: 'cancelled', label: 'Files', count: 4 }, { value: 'x', label: 'Actions', dot: true }]} />
                            <Card style={{ flex: 1 }} title="Vertical tabs">
                                Used for the case detail sub-navigation.
                            </Card>
                        </div>
                    </div>
                )}

                {tab === 'feedback' && (
                    <div className={s.stack}>
                        <Banner>Neutral banner with default icon.</Banner>
                        <Banner tone="info" title="Heads up">
                            Informational message with a title.
                        </Banner>
                        <Banner tone="good">Saved successfully.</Banner>
                        <Banner tone="warn" actions={<Button size="sm">Review</Button>}>
                            3 clients have unapproved lenders.
                        </Banner>
                        <Banner tone="bad" title="Could not load clients">
                            The API returned 500. Try again or contact support.
                        </Banner>
                        <div className={s.two}>
                            <EmptyState title="No cases yet" description="Cases appear here once a client signs." action={<Button size="sm" variant="primary">Add client</Button>} />
                            <EmptyState tone="error" title="Failed to load" description="Check the API server is running." action={<Button size="sm">Retry</Button>} />
                        </div>
                        <div className={s.row}>
                            <Spinner />
                            <Spinner size="md" text="Loading…" />
                            <Spinner size="lg" />
                        </div>
                        <CodeBlock maxHeight={140}>{`import { Page, PageHeader, Card } from 'ui'\n\nexport const MyView = () => (\n    <Page>\n        <PageHeader title="My page" />\n        <Card title="Panel">…</Card>\n    </Page>\n)`}</CodeBlock>
                    </div>
                )}
            </Section>

            <Modal
                open={modal}
                onClose={() => setModal(false)}
                title="Cancel claim"
                subtitle="SL-0048213 · Black Horse"
                footer={
                    <>
                        <Button onClick={() => setModal(false)}>Keep claim</Button>
                        <Button variant="danger" onClick={() => setModal(false)}>
                            Cancel claim
                        </Button>
                    </>
                }
            >
                <Banner tone="warn">This will notify the introducer and remove the claim from any open funding batch.</Banner>
                <Field label="Reason" required>
                    {(id) => <Textarea id={id} placeholder="Why is this claim being cancelled?" />}
                </Field>
            </Modal>

            <Drawer open={drawer} onClose={() => setDrawer(false)} title="Amelia Hart" subtitle="amelia.hart@example.com" actions={<Button size="sm" icon={<FaDownload />}>Export</Button>}>
                <div className={s.stack}>
                    <StatGrid minColumn={140}>
                        <StatCard compact label="Claims" value={3} />
                        <StatCard compact label="In batch" value={2} tone="good" />
                        <StatCard compact label="Removed" value={1} tone="bad" />
                    </StatGrid>
                    <KeyValueList columns items={[{ label: 'Client ref', value: 'SL-0048213', mono: true }, { label: 'Introducer', value: 'SRDS' }, { label: 'Signed', value: '02 Aug 2026' }, { label: 'Postcode', value: 'GL50 1JN' }]} />
                    <DataTable dense columns={COLUMNS.slice(1)} rows={ROWS} rowKey={(r) => r.id} />
                </div>
            </Drawer>
        </Page>
    )
}

export default StyleGuideView
